/**
 * Structural filter, generalised to k numbers out of n.
 *
 * None of these patterns is less likely to be drawn. They are massively more
 * PLAYED: combinations that form a line or a run on the betting slip are picked
 * far more often than their weight justifies. Rejecting them lowers the expected
 * number of co-winners, nothing else.
 */
import type { GameConfig } from './games';

export interface StructuralStats {
  sum: number;
  /** How many numbers are <= 31, i.e. usable as a day of the month. */
  dateCount: number;
  oddCount: number;
  decades: number;
  longestRun: number;
  /** Expected sum for a uniform draw. */
  sumMean: number;
  sumSd: number;
}

export function structuralStats(combo: readonly number[], g: GameConfig): StructuralStats {
  const c = [...combo].sort((a, b) => a - b);
  let sum = 0;
  let dateCount = 0;
  let oddCount = 0;
  const decades = new Set<number>();
  for (const x of c) {
    sum += x;
    if (x <= 31) dateCount++;
    if (x % 2 === 1) oddCount++;
    decades.add(Math.floor((x - 1) / 10));
  }
  let run = 1;
  let longestRun = 1;
  for (let i = 1; i < c.length; i++) {
    run = c[i] === (c[i - 1] as number) + 1 ? run + 1 : 1;
    if (run > longestRun) longestRun = run;
  }
  return {
    sum,
    dateCount,
    oddCount,
    decades: decades.size,
    longestRun,
    sumMean: (g.k * (g.n + 1)) / 2,
    sumSd: Math.sqrt((g.k * (g.n + 1) * (g.n - g.k)) / 12),
  };
}

/** Half-width of the band of acceptable sums, in standard deviations. */
export const SUM_BAND_SD = 1.15;

export function structuralOk(combo: readonly number[], g: GameConfig): boolean {
  const { k, n } = g;
  if (combo.length !== k) return false;
  const c = [...combo].sort((a, b) => a - b);
  for (let i = 0; i < k; i++) {
    const x = c[i] as number;
    if (!Number.isInteger(x) || x < 1 || x > n) return false;
    if (i > 0 && x === c[i - 1]) return false; // no duplicates
  }

  const s = structuralStats(c, g);

  // A birthday grid: too many numbers usable as a day of the month.
  if (s.dateCount > Math.ceil(k * 0.6)) return false;
  // ...and enough high numbers not to be one.
  if (k - s.dateCount < Math.max(1, Math.floor(k / 3))) return false;
  // No three consecutive numbers.
  if (s.longestRun >= 3) return false;
  // Spread across the slip.
  if (s.decades < Math.min(3, Math.floor((n + 9) / 10))) return false;
  // No arithmetic progression: all gaps equal.
  const gaps = new Set<number>();
  for (let i = 0; i + 1 < k; i++) gaps.add((c[i + 1] as number) - (c[i] as number));
  if (gaps.size === 1) return false;
  // Sum inside the central band.
  if (
    s.sum < s.sumMean - SUM_BAND_SD * s.sumSd ||
    s.sum > s.sumMean + SUM_BAND_SD * s.sumSd
  ) {
    return false;
  }
  // Neither all even nor all odd.
  if (s.oddCount === 0 || s.oddCount === k) return false;

  return true;
}
